import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AppStateShape } from '../../state/appState';
import type {
  DisplacementComponent,
  LocalAOI,
  StoredProduct,
} from '../../types';
import { api, APIError } from '../../api/client';
import {
  analyzeStaticPolygon,
  loadStaticCatalog,
  renderStaticInterferogram,
  renderStaticProduct,
  staticRadarPoints,
  staticStoredProducts,
  type StaticCatalog,
} from '../../static/staticCatalog';
import { Spinner } from '../ui/Spinner';
import './Sidebar.css';

interface Props {
  state: AppStateShape;
}

export function Sidebar({ state }: Props) {
  const [workspaceMode, setWorkspaceMode] = useState<'BUILD' | 'PRODUCTION'>('PRODUCTION');
  const [productionDisplacementEnabled, setProductionDisplacementEnabled] = useState(true);
  const [productionInterferogramEnabled, setProductionInterferogramEnabled] = useState(false);
  const [productionProductId, setProductionProductId] = useState('');
  const [jobInfo, setJobInfo] = useState<{
    jobId: string;
    hyp3JobId?: string | null;
    status: string;
    message: string;
    progress: number;
  } | null>(null);
  const [localAois, setLocalAois] = useState<LocalAOI[]>([]);
  const [staticCatalog, setStaticCatalog] = useState<StaticCatalog | null>(null);
  const [selectedAoiId, setSelectedAoiId] = useState('');
  const [activeAoiId, setActiveAoiId] = useState('');
  const [storedProducts, setStoredProducts] = useState<StoredProduct[]>([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [staticExport, setStaticExport] = useState<{
    viewerUrl: string;
    exportDir: string;
    pointCount: number;
    sizeMb: number;
  } | null>(null);
  const {
    filters, setFilters,
    setScenes, scenes,
    setLayer, layer,
    layerVisible, setLayerVisible,
    layerOpacity, setLayerOpacity,
    layerContrast, setLayerContrast,
    layerSaturation, setLayerSaturation,
    colorThresholdCmYr, setColorThresholdCmYr,
    colorMaxCmYr, setColorMaxCmYr,
    filterBelowThreshold, setFilterBelowThreshold,
    setRadarPoints, radarPoints, radarPointsVisible, setRadarPointsVisible,
    drawnPolygon, setDrawnPolygon, setDrawMode,
    polygonResult, setPolygonResult,
    setHighlightedPointIds,
    busy, setBusy,
    setError,
  } = state;
  const preferredInitialAoi = (aois: LocalAOI[]) => (
    aois.find((aoi) => /ministro|hales|codelco/i.test(aoi.name)) ?? aois[0]
  );

  const run = useCallback(
    async <T,>(key: string, fn: () => Promise<T>, onDone?: (res: T) => void) => {
      setBusy(key, true);
      setError(null);
      try {
        const res = await fn();
        await Promise.resolve(onDone?.(res));
      } catch (e) {
        const msg = e instanceof APIError ? e.message : (e as Error).message;
        setError(`${key}: ${msg}`);
      } finally {
        setBusy(key, false);
      }
    },
    [setBusy, setError],
  );

  const refreshProducts = useCallback(() => {
    if (workspaceMode === 'PRODUCTION' && staticCatalog) {
      loadStaticCatalog(true)
        .then((catalog) => {
          if (!catalog) return;
          setStaticCatalog(catalog);
          setStoredProducts(staticStoredProducts(catalog));
        })
        .catch(() => undefined);
      return;
    }
    api.storedProducts()
      .then((res) => {
        setStoredProducts(res.products);
      })
      .catch(() => undefined);
  }, [staticCatalog, workspaceMode]);

  const refreshLocalAois = useCallback(() => {
    if (workspaceMode === 'PRODUCTION' && staticCatalog) {
      loadStaticCatalog(true)
        .then((catalog) => {
          if (!catalog) return;
          setStaticCatalog(catalog);
          setLocalAois(catalog.aois);
          setStoredProducts(staticStoredProducts(catalog));
          setSelectedAoiId((prev) => (
            catalog.aois.some((aoi) => aoi.id === prev)
              ? prev
              : preferredInitialAoi(catalog.aois)?.id || ''
          ));
        })
        .catch(() => undefined);
      return;
    }
    api.localAois()
      .then((res) => {
        setLocalAois(res.aois);
        setSelectedAoiId((prev) => prev || preferredInitialAoi(res.aois)?.id || '');
      })
      .catch(() => undefined);
  }, [staticCatalog, workspaceMode]);

  useEffect(() => {
    loadStaticCatalog().then((catalog) => {
      if (catalog) {
        setStaticCatalog(catalog);
        setLocalAois(catalog.aois);
        setStoredProducts(staticStoredProducts(catalog));
        const initialAoi = preferredInitialAoi(catalog.aois);
        const initialAoiId = initialAoi?.id || '';
        setSelectedAoiId((prev) => prev || initialAoiId);
        setActiveAoiId((prev) => prev || initialAoiId);
        if (initialAoi) {
          const initialProductId = initialAoi.static_product_id || '';
          const initialProduct = initialProductId ? catalog.products[initialProductId]?.product : null;
          setFilters({
            bbox: initialAoi.bbox,
            ...(initialProduct?.start_date ? { startDate: initialProduct.start_date } : {}),
            ...(initialProduct?.end_date ? { endDate: initialProduct.end_date } : {}),
            ...(initialProduct ? { displacementComponent: chooseComponentForProduct(initialProduct) } : {}),
          });
          setDrawnPolygon(initialAoi.coordinates);
          setDrawMode('DONE');
          setSelectedProductId(initialProductId);
          setProductionProductId(initialProductId);
          if (initialProductId && initialProduct) {
            const component = chooseComponentForProduct(initialProduct);
            renderStaticProduct(catalog, initialProductId, component, {
              threshold_cm_yr: colorThresholdCmYr,
              color_max_cm_yr: colorMaxCmYr,
              filter_below_threshold: filterBelowThreshold,
              start_date: initialProduct.start_date,
              end_date: initialProduct.end_date,
            })
              .then((rendered) => {
                setLayer({ kind: 'DISPLACEMENT', data: rendered });
                setFilters({ bbox: rendered.overlay.bbox, displacementComponent: rendered.component });
              })
              .catch(() => undefined);
          }
        }
      } else {
        api.localAois()
          .then((res) => {
            setLocalAois(res.aois);
            setSelectedAoiId((prev) => prev || preferredInitialAoi(res.aois)?.id || '');
          })
          .catch(() => undefined);
        api.storedProducts()
          .then((res) => setStoredProducts(res.products))
          .catch(() => undefined);
      }
    });
  }, []);

  useEffect(() => {
    if (workspaceMode === 'BUILD') {
      api.localAois()
        .then((res) => {
          setLocalAois(res.aois);
          setSelectedAoiId((prev) => (
            res.aois.some((aoi) => aoi.id === prev)
              ? prev
              : preferredInitialAoi(res.aois)?.id || ''
          ));
        })
        .catch(() => undefined);
      api.storedProducts()
        .then((res) => setStoredProducts(res.products))
        .catch(() => undefined);
    } else if (staticCatalog) {
      setLocalAois(staticCatalog.aois);
      setStoredProducts(staticStoredProducts(staticCatalog));
    }
  }, [workspaceMode, staticCatalog]);

  const activeAoi = useMemo(() => (
    localAois.find((aoi) => aoi.id === activeAoiId)
    ?? localAois.find((aoi) => aoi.id === selectedAoiId)
    ?? null
  ), [activeAoiId, localAois, selectedAoiId]);

  const activeAoiBbox = activeAoi?.bbox ?? filters.bbox;

  const productsForCurrentAoi = useMemo(() => {
    const matchingProducts = storedProducts
      .filter((p) => p.bbox && productOverlapScore(p, activeAoiBbox) > 0)
      .sort((a, b) => {
        const overlapDiff = productOverlapScore(b, activeAoiBbox) - productOverlapScore(a, activeAoiBbox);
        if (Math.abs(overlapDiff) > 0.05) return overlapDiff;
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      });
    const pinnedId = jobInfo?.status === 'SUCCEEDED' ? jobInfo.jobId : selectedProductId;
    const pinnedProduct = storedProducts.find((p) => p.local_id === pinnedId);
    if (pinnedProduct && !matchingProducts.some((p) => p.local_id === pinnedProduct.local_id)) {
      return [pinnedProduct, ...matchingProducts];
    }
    return matchingProducts;
  }, [storedProducts, activeAoiBbox, jobInfo?.jobId, jobInfo?.status, selectedProductId]);

  const latestProductForCurrentAoi = useMemo(() => (
    latestProductForBbox(storedProducts, activeAoiBbox)
  ), [storedProducts, activeAoiBbox]);

  const productionProduct = useMemo(() => (
    storedProducts.find((p) => p.local_id === productionProductId) ?? latestProductForCurrentAoi
  ), [storedProducts, productionProductId, latestProductForCurrentAoi]);

  useEffect(() => {
    setSelectedProductId((prev) => (
      productsForCurrentAoi.some((p) => p.local_id === prev)
        ? prev
        : productsForCurrentAoi[0]?.local_id || ''
    ));
  }, [productsForCurrentAoi]);

  useEffect(() => {
    setProductionProductId(latestProductForCurrentAoi?.local_id || '');
  }, [latestProductForCurrentAoi]);

  const onSearchScenes = () =>
    run('search-scenes', () => api.searchScenes({
      bbox: filters.bbox,
      start_date: filters.startDate,
      end_date: filters.endDate,
      max_cloud_cover: filters.maxCloudCover,
    }), (res) => setScenes(res.scenes));

  const onImportKml = (file: File | undefined) => {
    if (!file) return;
    run('import-kml', () => api.importKml(file), (res) => {
      setFilters({ bbox: res.bbox });
      setDrawnPolygon(res.coordinates);
      setDrawMode('DONE');
      setPolygonResult(null);
      setHighlightedPointIds(new Set());
      setLayer({ kind: 'NONE' });
      setRadarPoints([]);
      setScenes([]);
      setActiveAoiId('uploaded');
      setSelectedProductId('');
    });
  };

  const applyAoi = (res: LocalAOI) => {
    setFilters({ bbox: res.bbox });
    setDrawnPolygon(res.coordinates);
    setDrawMode('DONE');
    setPolygonResult(null);
    setHighlightedPointIds(new Set());
    setLayer({ kind: 'NONE' });
    setRadarPoints([]);
    setScenes([]);
    setActiveAoiId(res.id);
    setSelectedProductId('');
  };

  const chooseComponentForProduct = (product?: StoredProduct | null): DisplacementComponent => {
    if (product?.has_vertical) return 'VERTICAL';
    if (product?.has_los) return 'LOS';
    return filters.displacementComponent;
  };

  const componentForProduct = (
    product?: StoredProduct | null,
    preferred: DisplacementComponent = filters.displacementComponent,
  ): DisplacementComponent => {
    if (!product) return preferred;
    if (preferred === 'VERTICAL' && product.has_vertical) return 'VERTICAL';
    if (preferred === 'LOS' && product.has_los) return 'LOS';
    return chooseComponentForProduct(product);
  };

  const staticScale = () => ({
    threshold_cm_yr: colorThresholdCmYr,
    color_max_cm_yr: colorMaxCmYr,
    filter_below_threshold: filterBelowThreshold,
    start_date: filters.startDate,
    end_date: filters.endDate,
  });

  const onLoadLocalAoi = () => {
    if (!selectedAoiId) {
      setError('No hay ningun sitio local seleccionado.');
      return;
    }
    run('load-local-aoi', () => api.localAoi(selectedAoiId), applyAoi);
  };

  const onLoadProductionAoi = (aoiId: string) => {
    setSelectedAoiId(aoiId);
    if (!aoiId) return;
    if (staticCatalog) {
      const res = staticCatalog.aois.find((aoi) => aoi.id === aoiId);
      if (!res) return;
      applyAoi(res);
      const productId = res.static_product_id || latestProductForBbox(staticStoredProducts(staticCatalog), res.bbox)?.local_id || '';
      setProductionProductId(productId);
      setSelectedProductId(productId);
      if (productionDisplacementEnabled && productId) {
        const product = staticCatalog.products[productId]?.product;
        const component = chooseComponentForProduct(product);
        setFilters({
          displacementComponent: component,
          ...(product?.start_date ? { startDate: product.start_date } : {}),
          ...(product?.end_date ? { endDate: product.end_date } : {}),
        });
        run('load-production-aoi', async () => renderStaticProduct(staticCatalog, productId, component, staticScale()), (rendered) => {
          setLayer({ kind: 'DISPLACEMENT', data: rendered });
          setFilters({ bbox: rendered.overlay.bbox });
          setRadarPoints([]);
          setRadarPointsVisible(false);
        });
      }
      return;
    }
    run('load-production-aoi', () => api.localAoi(aoiId), async (res) => {
      applyAoi(res);
      const latestProducts = await api.storedProducts();
      setStoredProducts(latestProducts.products);
      const latest = latestProductForBbox(latestProducts.products, res.bbox);
      setProductionProductId(latest?.local_id || '');
      setSelectedProductId(latest?.local_id || '');
      if (productionDisplacementEnabled && latest) {
        const rendered = await loadFirstRenderableProductionProduct(res.bbox, latestProducts.products);
        setLayer({ kind: 'DISPLACEMENT', data: rendered });
        setFilters({ bbox: rendered.overlay.bbox });
        setRadarPoints([]);
        setRadarPointsVisible(false);
      }
    });
  };

  const onComputeDisplacement = async () => {
    setBusy('compute-displacement', true);
    setError(null);
    setRadarPoints([]);
    try {
      const submitted = await api.submitVelocityJob({
        bbox: filters.bbox,
        start_date: filters.startDate,
        end_date: filters.endDate,
        component: filters.displacementComponent,
        decompose: filters.decompose,
        threshold_cm_yr: colorThresholdCmYr,
        color_max_cm_yr: colorMaxCmYr,
        filter_below_threshold: filterBelowThreshold,
      });
      setJobInfo({
        jobId: submitted.job_id,
        status: submitted.status,
        message: submitted.message,
        progress: 0,
      });

      let status = await api.velocityJobStatus(submitted.job_id);
      while (status.status === 'QUEUED' || status.status === 'RUNNING') {
        setJobInfo({
          jobId: status.job_id,
          hyp3JobId: status.hyp3_job_id,
          status: status.status,
          message: status.message,
          progress: status.progress,
        });
        await new Promise((resolve) => window.setTimeout(resolve, 15000));
        status = await api.velocityJobStatus(submitted.job_id);
      }
      setJobInfo({
        jobId: status.job_id,
        hyp3JobId: status.hyp3_job_id,
        status: status.status,
        message: status.message,
        progress: status.progress,
      });

      if (status.status === 'FAILED' || !status.result) {
        throw new Error(status.error || status.message || 'Fallo el procesamiento real de HyP3.');
      }

      setLayer({ kind: 'DISPLACEMENT', data: status.result });
      setFilters({ bbox: status.result.overlay.bbox });
      setSelectedProductId(status.job_id);
      setProductionProductId(status.job_id);
      setRadarPoints([]);
      setRadarPointsVisible(false);
      const latestProducts = await api.storedProducts();
      setStoredProducts(latestProducts.products);
      setSelectedProductId(status.job_id);
      setProductionProductId(status.job_id);
    } catch (e) {
      const msg = e instanceof APIError ? e.message : (e as Error).message;
      setError(`real-displacement: ${msg}`);
    } finally {
      setBusy('compute-displacement', false);
    }
  };

  const onLoadStoredProduct = () => {
    if (!selectedProductId) {
      setError('No hay ningun producto HyP3 guardado seleccionado.');
      return;
    }
    const selected = storedProducts.find((p) => p.local_id === selectedProductId);
    if (selected?.bbox && productOverlapScore(selected, filters.bbox) <= 0) {
      setError('load-stored-product: el producto seleccionado no se superpone con el sitio actual. Elige un producto con coincidencia de area > 0%, o carga primero el sitio correspondiente.');
      return;
    }
    const productComponent = componentForProduct(selected);
    run('load-stored-product', () => loadProductById(selectedProductId, filters.bbox, selected, productComponent), async (res) => {
      setLayer({ kind: 'DISPLACEMENT', data: res });
      setFilters({ bbox: res.overlay.bbox, displacementComponent: res.component });
      if (drawnPolygon && !ringIntersectsBBox(drawnPolygon, res.overlay.bbox)) {
        setDrawnPolygon(null);
        setDrawMode('NONE');
        setPolygonResult(null);
        setHighlightedPointIds(new Set());
      }
      setRadarPoints([]);
      setRadarPointsVisible(false);
    });
  };

  const loadProductById = (
    productId: string,
    bbox: [number, number, number, number],
    product?: StoredProduct | null,
    component: DisplacementComponent = chooseComponentForProduct(product),
  ) => {
    const safeComponent = componentForProduct(product, component);
    if (staticCatalog?.products[productId]) {
      return renderStaticProduct(staticCatalog, productId, safeComponent, staticScale());
    }
    return api.renderStoredProduct(productId, {
      bbox,
      start_date: product?.start_date || filters.startDate,
      end_date: product?.end_date || filters.endDate,
      component: safeComponent,
      decompose: filters.decompose,
      threshold_cm_yr: colorThresholdCmYr,
      color_max_cm_yr: colorMaxCmYr,
      filter_below_threshold: filterBelowThreshold,
    });
  };

  const loadFirstRenderableProductionProduct = async (
    bbox: [number, number, number, number],
    products: StoredProduct[],
  ) => {
    const candidates = products
      .filter((p) => p.bbox && productOverlapScore(p, bbox) > 0)
      .sort((a, b) => {
        const overlapDiff = productOverlapScore(b, bbox) - productOverlapScore(a, bbox);
        if (Math.abs(overlapDiff) > 0.05) return overlapDiff;
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      });
    let lastError = '';
    for (const product of candidates) {
      try {
        const rendered = await loadProductById(product.local_id, bbox, product);
        setProductionProductId(product.local_id);
        setSelectedProductId(product.local_id);
        return rendered;
      } catch (e) {
        lastError = e instanceof APIError ? e.message : (e as Error).message;
      }
    }
    throw new Error(lastError || 'No hay producto de desplazamiento valido para este sitio.');
  };

  const onToggleProductionDisplacement = (enabled: boolean) => {
    setProductionDisplacementEnabled(enabled);
    setLayerVisible(enabled);
    if (!enabled) return;
    if (staticCatalog) {
      const product = latestProductForCurrentAoi;
      if (!product) {
        setError('No hay producto de desplazamiento procesado para este sitio.');
        return;
      }
      setProductionProductId(product.local_id);
      setSelectedProductId(product.local_id);
      const component = chooseComponentForProduct(product);
      setFilters({
        displacementComponent: component,
        ...(product.start_date ? { startDate: product.start_date } : {}),
        ...(product.end_date ? { endDate: product.end_date } : {}),
      });
      run('load-production-product', () => renderStaticProduct(staticCatalog, product.local_id, component, staticScale()), (res) => {
        setLayer({ kind: 'DISPLACEMENT', data: res });
        setFilters({ bbox: res.overlay.bbox });
        setRadarPoints([]);
        setRadarPointsVisible(false);
      });
      return;
    }
    run('load-production-product', async () => {
      const latestProducts = await api.storedProducts();
      setStoredProducts(latestProducts.products);
      const product = latestProductForBbox(latestProducts.products, filters.bbox);
      if (!product) {
        throw new Error('No hay producto de desplazamiento procesado para este sitio.');
      }
      return loadFirstRenderableProductionProduct(filters.bbox, latestProducts.products);
    }, (res) => {
      setLayer({ kind: 'DISPLACEMENT', data: res });
      setFilters({ bbox: res.overlay.bbox });
      setRadarPoints([]);
      setRadarPointsVisible(false);
    });
  };

  const onApplyColorScale = () => {
    if (!selectedProductId || layer.kind !== 'DISPLACEMENT') {
      setError('Carga un producto de desplazamiento procesado antes de aplicar la escala de color.');
      return;
    }
    const selected = storedProducts.find((p) => p.local_id === selectedProductId);
    run('apply-color-scale', () => loadProductById(selectedProductId, layer.data.overlay.bbox, selected, layer.data.component), (res) => {
      setLayer({ kind: 'DISPLACEMENT', data: res });
      setFilters({ bbox: res.overlay.bbox });
    });
  };

  const onProductionProductChange = (productId: string) => {
    setProductionProductId(productId);
    setSelectedProductId(productId);
    const selected = storedProducts.find((p) => p.local_id === productId);
    if (!selected) return;
    const component = chooseComponentForProduct(selected);
    setFilters({
      displacementComponent: component,
      ...(selected.start_date ? { startDate: selected.start_date } : {}),
      ...(selected.end_date ? { endDate: selected.end_date } : {}),
    });
    run('load-production-product', () => loadProductById(productId, selected.bbox || filters.bbox, selected, component), (res) => {
      setLayer({ kind: 'DISPLACEMENT', data: res });
      setFilters({ bbox: res.overlay.bbox });
      setRadarPoints([]);
      setRadarPointsVisible(false);
    });
  };

  const onResetVisualFilters = useCallback(() => {
    setLayerContrast(115);
    setLayerSaturation(125);
  }, [setLayerContrast, setLayerSaturation]);

  const onExportStaticProduct = () => {
    if (!selectedProductId || layer.kind !== 'DISPLACEMENT') {
      setError('Carga un producto de desplazamiento antes de exportarlo como estatico.');
      return;
    }
    run('export-static-product', () => api.exportStaticStoredProduct(selectedProductId, {
      bbox: layer.data.overlay.bbox,
      start_date: filters.startDate,
      end_date: filters.endDate,
      component: layer.data.component,
      decompose: filters.decompose,
      threshold_cm_yr: colorThresholdCmYr,
      color_max_cm_yr: colorMaxCmYr,
      filter_below_threshold: filterBelowThreshold,
    }), (res) => {
      setStaticExport({
        viewerUrl: res.viewer_url,
        exportDir: res.export_dir,
        pointCount: res.point_count,
        sizeMb: res.size_mb,
      });
      window.open(res.viewer_url, '_blank', 'noopener,noreferrer');
    });
  };

  const onComputeInterferogram = (preserveRadarPoints = false) =>
    run('compute-interferogram', () => api.computeInterferogram({
      bbox: filters.bbox,
      start_date: filters.startDate,
      end_date: filters.endDate,
      pair_index: filters.interferogramPairIndex,
    }), async (res) => {
      setLayer({ kind: 'INTERFEROGRAM', data: res });
      setFilters({ bbox: res.overlay.bbox });
      if (!preserveRadarPoints) {
        setRadarPoints([]);
        setRadarPointsVisible(false);
      }
    });

  const onToggleProductionInterferogram = (enabled: boolean) => {
    setProductionInterferogramEnabled(enabled);
    if (enabled) {
      if (staticCatalog) {
        if (!selectedProductId) {
          setProductionInterferogramEnabled(false);
          setError('Carga primero un producto de desplazamiento.');
          return;
        }
        run('compute-interferogram', () => renderStaticInterferogram(staticCatalog, selectedProductId), (res) => {
          setLayer({ kind: 'INTERFEROGRAM', data: res });
          setFilters({ bbox: res.overlay.bbox });
        });
        return;
      }
      onComputeInterferogram(true);
      return;
    }
    if (!selectedProductId) return;
    const selected = storedProducts.find((p) => p.local_id === selectedProductId);
    run('load-production-displacement', () => loadProductById(selectedProductId, filters.bbox, selected), (res) => {
      setLayer({ kind: 'DISPLACEMENT', data: res });
      setFilters({ bbox: res.overlay.bbox });
      setLayerVisible(true);
    });
  };

  const onAnalyzePolygon = () => {
    if (!drawnPolygon || drawnPolygon.length < 3) {
      setError('Dibuja primero un poligono en el mapa.');
      return;
    }
    const selected = storedProducts.find((p) => p.local_id === selectedProductId);
    const productComponent = filters.displacementComponent;
    const rasterBBox = layer.kind === 'DISPLACEMENT'
      ? layer.data.overlay.bbox
      : selected?.bbox;
    const canUseRealRaster = Boolean(selectedProductId && rasterBBox);
    if (layer.kind === 'INTERFEROGRAM' && !canUseRealRaster) {
      setError('analyze-polygon: los interferogramas envueltos son capas visuales de QA de fase. Selecciona o carga un producto de desplazamiento para calcular estadisticas del poligono.');
      return;
    }
    if (canUseRealRaster && rasterBBox && !ringIntersectsBBox(drawnPolygon, rasterBBox)) {
      setError('analyze-polygon: el poligono no se superpone con el raster de desplazamiento cargado. Dibuja un poligono dentro de la capa visible.');
      return;
    }
    const request = {
      coordinates: drawnPolygon,
      component: productComponent,
      start_date: filters.startDate,
      end_date: filters.endDate,
    };
    if (staticCatalog && selectedProductId && staticCatalog.products[selectedProductId]) {
      run('analyze-polygon', async () => {
        const pts = await staticRadarPoints(staticCatalog, selectedProductId);
        const inside = pts.points.filter((p) => pointInRing([p.lon, p.lat], drawnPolygon));
        setRadarPoints(inside);
        setRadarPointsVisible(true);
        return analyzeStaticPolygon(staticCatalog, selectedProductId, drawnPolygon);
      }, (res) => {
        setPolygonResult(res);
        setHighlightedPointIds(new Set(res.points.map((p) => p.point_id)));
      });
      return;
    }
    run('analyze-polygon', async () => {
      if (canUseRealRaster && selectedProductId) {
        const pts = await api.storedProductRadarPoints(selectedProductId, {
          bbox: ringBBox(drawnPolygon),
          component: productComponent,
          max_points: 12000,
        });
        const inside = pts.points.filter((p) => pointInRing([p.lon, p.lat], drawnPolygon));
        setRadarPoints(inside);
        setRadarPointsVisible(true);
        return api.storedProductPolygon(selectedProductId, request);
      }
      return api.polygonAnalyze(request);
    }, (res) => {
      setPolygonResult(res);
      setHighlightedPointIds(new Set(res.points.map((p) => p.point_id)));
    });
  };

  const onStartProductionPolygon = () => {
    if (layer.kind !== 'DISPLACEMENT') {
      setError('Carga primero el mapa de desplazamiento para dibujar el poligono de analisis.');
      return;
    }
    setDrawnPolygon(null);
    setPolygonResult(null);
    setHighlightedPointIds(new Set());
    setRadarPoints([]);
    setDrawMode('DRAWING');
  };

  const onFinishProductionPolygon = () => {
    window.dispatchEvent(new Event('finish-polygon-drawing'));
  };

  const onExportCSV = () => {
    if (!drawnPolygon || drawnPolygon.length < 3) {
      setError('Dibuja primero un poligono.');
      return;
    }
    run('export-csv', () => api.exportPolygonCSV({
      coordinates: drawnPolygon,
      component: filters.displacementComponent,
      start_date: filters.startDate,
      end_date: filters.endDate,
    }));
  };

  return (
    <aside className="sidebar">
      <div className="workspace-tabs" role="tablist" aria-label="Modo de trabajo">
        <button
          type="button"
          className={workspaceMode === 'PRODUCTION' ? 'active' : ''}
          onClick={() => setWorkspaceMode('PRODUCTION')}
        >
          Visualizacion
        </button>
        <button
          type="button"
          className={workspaceMode === 'BUILD' ? 'active' : ''}
          onClick={() => setWorkspaceMode('BUILD')}
        >
          Nuevo procesamiento
        </button>
      </div>

      {workspaceMode === 'PRODUCTION' ? (
        <ProductionPanel
          localAois={localAois}
          selectedAoiId={selectedAoiId}
          activeAoiId={activeAoiId}
          selectedProduct={productionProduct}
          productionProducts={productsForCurrentAoi}
          productionProductId={productionProductId}
          onProductChange={onProductionProductChange}
          startDate={filters.startDate}
          endDate={filters.endDate}
          onDateRangeChange={(startDate, endDate) => setFilters({ startDate, endDate })}
          staticBuild={Boolean(staticCatalog)}
          busy={busy}
          layer={layer}
          drawnPolygon={drawnPolygon}
          polygonResult={polygonResult}
          colorThresholdCmYr={colorThresholdCmYr}
          colorMaxCmYr={colorMaxCmYr}
          filterBelowThreshold={filterBelowThreshold}
          setColorThresholdCmYr={setColorThresholdCmYr}
          setColorMaxCmYr={setColorMaxCmYr}
          setFilterBelowThreshold={setFilterBelowThreshold}
          layerContrast={layerContrast}
          layerSaturation={layerSaturation}
          setLayerContrast={setLayerContrast}
          setLayerSaturation={setLayerSaturation}
          onResetVisualFilters={onResetVisualFilters}
          onSelectAoi={onLoadProductionAoi}
          onRefreshAois={refreshLocalAois}
          onRefreshProducts={refreshProducts}
          onToggleDisplacement={onToggleProductionDisplacement}
          onApplyColorScale={onApplyColorScale}
          onToggleInterferogram={onToggleProductionInterferogram}
          interferogramEnabled={productionInterferogramEnabled}
          onAnalyzePolygon={onAnalyzePolygon}
          onStartPolygon={onStartProductionPolygon}
          onFinishPolygon={onFinishProductionPolygon}
        />
      ) : (
        <>
      <div className="section build-context">
        <span className="eyebrow">Procesamiento</span>
        <h3>Generacion de productos</h3>
        <p>
          Esta area es para crear nuevos GeoTIFFs y productos SAR en el backend. Usala cuando necesites procesar un AOI; para ver resultados ya publicados, vuelve a Visualizacion.
        </p>
      </div>

      {/* ---- Scene query ---- */}
      <div className="section">
        <h3 className="section-title">
          <span className="section-num">01</span>
          AOI + Busqueda de escenas
        </h3>
        <div className="field">
          <label htmlFor="local-aoi">Carpeta local de AOI</label>
          <select
            id="local-aoi"
            value={selectedAoiId}
            onChange={(e) => setSelectedAoiId(e.target.value)}
          >
            {localAois.length === 0 && <option value="">No se encontraron sitios en input(s)</option>}
            {localAois.map((aoi) => (
              <option key={aoi.id} value={aoi.id}>
                {aoi.name} - {aoi.path}
              </option>
            ))}
          </select>
        </div>
        <button
          className="btn btn-primary"
          onClick={onLoadLocalAoi}
          type="button"
          disabled={!selectedAoiId || busy['load-local-aoi']}
        >
          {busy['load-local-aoi'] ? <Spinner size={12} inline /> : null}
          Cargar AOI
        </button>
        <div className="field">
          <label htmlFor="kml-upload">Archivo del sitio</label>
          <input
            id="kml-upload"
            type="file"
            accept=".kml,.kmz,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
            onChange={(e) => onImportKml(e.target.files?.[0])}
          />
        </div>
        {busy['import-kml'] && <div className="kml-status"><Spinner size={12} inline /> Importando sitio...</div>}
        {drawnPolygon && (
          <div className="kml-status kml-ready">
            AOI cargado: {drawnPolygon.length} vertices. El bbox del mapa sigue este poligono.
          </div>
        )}
        <div className="row">
          <div className="field">
            <label htmlFor="start-date">Inicio</label>
            <input
              id="start-date"
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ startDate: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="end-date">Fin</label>
            <input
              id="end-date"
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ endDate: e.target.value })}
            />
          </div>
        </div>
        <ActionButton
          id="btn-search-scenes"
          label="Consultar escenas SAR"
          busy={busy['search-scenes']}
          onClick={onSearchScenes}
          icon="+"
        />
        {scenes.length > 0 && (
          <div className="scenes-summary">
            <span className="scenes-count">{scenes.length}</span>
            <span> escenas encontradas</span>
            <div className="scenes-tracks">
              <TrackBadge direction="ASC" count={scenes.filter(s => s.flight_direction === 'ASCENDING').length} />
              <TrackBadge direction="DESC" count={scenes.filter(s => s.flight_direction === 'DESCENDING').length} />
            </div>
          </div>
        )}
        {scenes.length > 0 && (
          <div className="scene-list">
            {scenes.slice(0, 5).map((s) => (
              <div key={s.scene_name} className="scene-item">
                <span className={`scene-dir ${s.flight_direction === 'ASCENDING' ? 'asc' : 'desc'}`}>
                  {s.flight_direction === 'ASCENDING' ? 'ASC' : 'DESC'}
                </span>
                <span className="scene-date">{s.start_time.slice(0, 10)}</span>
                <span className="scene-track">P{s.path_number} F{s.frame_number}</span>
              </div>
            ))}
            {scenes.length > 5 && (
              <div className="scene-item scene-more">+{scenes.length - 5} escenas mas</div>
            )}
          </div>
        )}
      </div>

      {/* ---- Displacement velocity ---- */}
      <div className="section">
        <h3 className="section-title">
          <span className="section-num">02</span>
          Mapa de desplazamiento
        </h3>
        <div className="field">
          <label htmlFor="disp-component">Componente</label>
          <select
            id="disp-component"
            value={filters.displacementComponent}
            onChange={(e) =>
              setFilters({ displacementComponent: e.target.value as DisplacementComponent })
            }
          >
            <option value="VERTICAL">Vertical (subsidencia / levantamiento)</option>
            <option value="EAST_WEST">Este-Oeste</option>
            <option value="LOS">LOS (linea de vista)</option>
          </select>
        </div>
        <div className="field">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={filters.decompose}
              onChange={(e) => setFilters({ decompose: e.target.checked })}
            />
            Descomposicion asc. + desc.
          </label>
        </div>
        <ActionButton
          id="btn-compute-displacement"
          label="Enviar job real de velocidad HyP3"
          busy={busy['compute-displacement']}
          onClick={onComputeDisplacement}
          primary
          icon="JOB"
        />
        {jobInfo && (
          <div className={`job-status ${jobInfo.status.toLowerCase()}`}>
            <div className="job-status-head">
              <strong>{jobInfo.status}</strong>
              <span>{Math.round(jobInfo.progress * 100)}%</span>
            </div>
            <div>{jobInfo.message}</div>
            <div className="job-status-id">local: {jobInfo.jobId}</div>
            {jobInfo.hyp3JobId && <div className="job-status-id">HyP3: {jobInfo.hyp3JobId}</div>}
          </div>
        )}
        <div className="field">
          <label htmlFor="stored-product">Producto procesado existente</label>
          <select
            id="stored-product"
            value={selectedProductId}
            onChange={(e) => setSelectedProductId(e.target.value)}
            disabled={productsForCurrentAoi.length === 0}
          >
            {productsForCurrentAoi.length === 0 && <option value="">No hay productos para el area actual</option>}
            {productsForCurrentAoi.map((p) => (
              <option key={p.local_id} value={p.local_id} disabled={!p.bbox}>
                {formatProductLabel(p, filters.bbox)}
              </option>
            ))}
          </select>
        </div>
        <div className="row">
          <button className="btn" onClick={refreshProducts} type="button">
            Actualizar productos
          </button>
          <button
            className="btn btn-primary"
            onClick={onLoadStoredProduct}
            type="button"
            disabled={!selectedProductId || busy['load-stored-product']}
          >
            {busy['load-stored-product'] ? <Spinner size={12} inline /> : null}
            Cargar existente
          </button>
        </div>
        <button
          className="btn btn-secondary full-btn"
          onClick={onExportStaticProduct}
          type="button"
          disabled={layer.kind !== 'DISPLACEMENT' || !selectedProductId || busy['export-static-product']}
        >
          {busy['export-static-product'] ? <Spinner size={12} inline /> : null}
          Exportar producto estatico
        </button>
        {staticExport && (
          <div className="static-export-box">
            <strong>Export listo</strong>
            <span>{staticExport.pointCount.toLocaleString()} puntos - {staticExport.sizeMb.toFixed(1)} MB</span>
            <a href={staticExport.viewerUrl} target="_blank" rel="noreferrer">Abrir visor estatico</a>
            <small>{staticExport.exportDir}</small>
          </div>
        )}
      </div>

      {/* ---- Interferogram ---- */}
      <div className="section">
        <h3 className="section-title">
          <span className="section-num">03</span>
          Interferograma
        </h3>
        <div className="field">
          <label htmlFor="pair-index">Indice del par (0 = mas reciente)</label>
          <input
            id="pair-index"
            type="number"
            min={0}
            max={10}
            value={filters.interferogramPairIndex}
            onChange={(e) => setFilters({ interferogramPairIndex: Number(e.target.value) })}
          />
        </div>
        <p className="section-note">
          Cada ciclo de color HSV = ~2.8 cm de movimiento LOS. Revisa la densidad de franjas y los patrones de coherencia.
          No equivale al mapa de velocidad desempaquetado.
        </p>
        <ActionButton
          id="btn-compute-interferogram"
          label="Cargar interferograma"
          busy={busy['compute-interferogram']}
          onClick={onComputeInterferogram}
          icon="IFG"
        />
        {layer.kind === 'INTERFEROGRAM' && (
          <div className="ifg-meta">
            <span className="ifg-pair">{layer.data.pair_label}</span>
            <span className="ifg-coh">coh: {layer.data.coherence_mean.toFixed(2)}</span>
            <span className="ifg-bl">linea base {layer.data.temporal_baseline_days}d</span>
          </div>
        )}
      </div>

      {/* ---- Polygon analysis ---- */}
      <div className="section">
        <h3 className="section-title">
          <span className="section-num">04</span>
          Analisis de poligono
        </h3>
        <p className="section-note">
          Dibuja un poligono en el mapa y calcula la serie temporal agregada de todos los puntos radar dentro.
        </p>
        <div className="polygon-status">
          {!drawnPolygon
            ? <span className="poly-hint">Aun no hay poligono: usa el boton Dibujar poligono en el mapa</span>
            : <span className="poly-ready">Poligono listo ({drawnPolygon.length} vertices)</span>
          }
        </div>
        <ActionButton
          id="btn-analyze-polygon"
          label="Analizar poligono"
          busy={busy['analyze-polygon']}
          onClick={onAnalyzePolygon}
          disabled={!drawnPolygon}
          primary
          icon="*"
        />
        {polygonResult && (
          <div className="polygon-summary">
            <div className="poly-stat">
              <span>Puntos dentro</span>
              <strong>{polygonResult.n_points}</strong>
            </div>
            <div className="poly-stat">
              <span>Area</span>
              <strong>{polygonResult.polygon_area_km2.toFixed(2)} km2</strong>
            </div>
            <div className="poly-stat">
              <span>Velocidad media</span>
              <strong className={polygonResult.mean_velocity_cm_yr < 0 ? 'v-neg' : 'v-pos'}>
                {polygonResult.mean_velocity_cm_yr > 0 ? '+' : ''}{polygonResult.mean_velocity_cm_yr.toFixed(2)} cm/yr
              </strong>
            </div>
          </div>
        )}
      </div>

      {/* ---- Layer manager ---- */}
      <div className="section layer-panel">
        <h3 className="section-title">
          <span className="section-num">05</span>
          Gestor de capas
        </h3>
        {layer.kind === 'NONE' ? (
          <div className="empty">Todavia no se calculo ninguna capa.</div>
        ) : (
          <>
            <div className="field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={layerVisible}
                  onChange={(e) => setLayerVisible(e.target.checked)}
                />
                Mostrar capa
              </label>
            </div>
            <div className="field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={radarPointsVisible}
                  disabled={radarPoints.length === 0}
                  onChange={(e) => setRadarPointsVisible(e.target.checked)}
                />
                Mostrar puntos radar ({radarPoints.length})
              </label>
            </div>
            <div className="field">
              <label>Opacidad ({Math.round(layerOpacity * 100)}%)</label>
              <input
                type="range"
                min={0} max={100}
                value={Math.round(layerOpacity * 100)}
                onChange={(e) => setLayerOpacity(Number(e.target.value) / 100)}
              />
            </div>
            <div className="visual-tuning">
              <div className="visual-tuning-head">
                <span>Escala de color</span>
                <button
                  type="button"
                  className="mini-btn"
                  onClick={() => {
                    setColorThresholdCmYr(3.45);
                    setColorMaxCmYr(12);
                    setFilterBelowThreshold(false);
                    setLayerContrast(115);
                    setLayerSaturation(125);
                  }}
                >
                  Reiniciar
                </button>
              </div>
              <VisualSlider label="Umbral critico" value={colorThresholdCmYr} min={0} max={12} step={0.05} unit=" cm/yr" onChange={setColorThresholdCmYr} />
              <VisualSlider label="Maximo de color" value={colorMaxCmYr} min={0.5} max={20} step={0.1} unit=" cm/yr" onChange={setColorMaxCmYr} />
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={filterBelowThreshold}
                  onChange={(e) => setFilterBelowThreshold(e.target.checked)}
                />
                Ocultar bajo el umbral
              </label>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy['apply-color-scale'] || layer.kind !== 'DISPLACEMENT'}
                onClick={onApplyColorScale}
              >
                {busy['apply-color-scale'] ? <Spinner size={12} inline /> : null}
                Aplicar escala
              </button>
              <div className="visual-tuning-head muted-head">
                <span>Filtro visual</span>
              </div>
              <VisualSlider label="Contraste" value={layerContrast} min={60} max={220} unit="%" onChange={setLayerContrast} />
              <VisualSlider label="Saturacion" value={layerSaturation} min={40} max={240} unit="%" onChange={setLayerSaturation} />
            </div>
            <LayerMeta state={state} />
          </>
        )}
      </div>

      {/* ---- Export ---- */}
      {polygonResult && (
        <div className="section">
          <h3 className="section-title">
            <span className="section-num">06</span>
            Exportar
          </h3>
          <div className="export-buttons">
            <button
              id="btn-export-csv"
              className="btn btn-export"
              onClick={onExportCSV}
              disabled={busy['export-csv']}
            >
              {busy['export-csv'] ? <Spinner size={12} inline /> : 'CSV'}
              &nbsp;Puntos radar CSV
            </button>
            <button
              id="btn-export-ts-csv"
              className="btn btn-export"
              onClick={() => {
                if (!drawnPolygon) return;
                run('export-ts-csv', () => api.exportTimeseriesCSV({
                  coordinates: drawnPolygon,
                  component: filters.displacementComponent,
                  start_date: filters.startDate,
                  end_date: filters.endDate,
                }));
              }}
              disabled={busy['export-ts-csv']}
            >
              {busy['export-ts-csv'] ? <Spinner size={12} inline /> : 'CSV'}
              &nbsp;Serie temporal CSV
            </button>
          </div>
        </div>
      )}
        </>
      )}
    </aside>
  );
}

function ProductionPanel({
  localAois,
  selectedAoiId,
  activeAoiId,
  selectedProduct,
  productionProducts,
  productionProductId,
  onProductChange,
  startDate,
  endDate,
  onDateRangeChange,
  staticBuild,
  busy,
  layer,
  drawnPolygon,
  polygonResult,
  colorThresholdCmYr,
  colorMaxCmYr,
  filterBelowThreshold,
  setColorThresholdCmYr,
  setColorMaxCmYr,
  setFilterBelowThreshold,
  layerContrast,
  layerSaturation,
  setLayerContrast,
  setLayerSaturation,
  onResetVisualFilters,
  onSelectAoi,
  onRefreshAois,
  onRefreshProducts,
  onToggleDisplacement,
  onApplyColorScale,
  onToggleInterferogram,
  interferogramEnabled,
  onAnalyzePolygon,
  onStartPolygon,
  onFinishPolygon,
}: {
  localAois: LocalAOI[];
  selectedAoiId: string;
  activeAoiId: string;
  selectedProduct?: StoredProduct | null;
  productionProducts: StoredProduct[];
  productionProductId: string;
  onProductChange: (productId: string) => void;
  startDate: string;
  endDate: string;
  onDateRangeChange: (startDate: string, endDate: string) => void;
  staticBuild: boolean;
  busy: AppStateShape['busy'];
  layer: AppStateShape['layer'];
  drawnPolygon: AppStateShape['drawnPolygon'];
  polygonResult: AppStateShape['polygonResult'];
  colorThresholdCmYr: number;
  colorMaxCmYr: number;
  filterBelowThreshold: boolean;
  setColorThresholdCmYr: (value: number) => void;
  setColorMaxCmYr: (value: number) => void;
  setFilterBelowThreshold: (value: boolean) => void;
  layerContrast: number;
  layerSaturation: number;
  setLayerContrast: (value: number) => void;
  setLayerSaturation: (value: number) => void;
  onResetVisualFilters: () => void;
  onSelectAoi: (aoiId: string) => void;
  onRefreshAois: () => void;
  onRefreshProducts: () => void;
  onToggleDisplacement: (enabled: boolean) => void;
  onApplyColorScale: () => void;
  onToggleInterferogram: (enabled: boolean) => void;
  interferogramEnabled: boolean;
  onAnalyzePolygon: () => void;
  onStartPolygon: () => void;
  onFinishPolygon: () => void;
}) {
  const hasLoadedAoi = Boolean(activeAoiId);
  const isDisplacementLoaded = layer.kind === 'DISPLACEMENT';
  const scaleControlsDisabled = !isDisplacementLoaded;

  return (
    <div className="production-panel">
      <div className="section production-hero">
        <div>
          <span className="eyebrow">Operacion</span>
          <h3>Monitoreo por sitio</h3>
        </div>
        <p>Selecciona un sitio y la plataforma carga automaticamente el ultimo producto disponible para esa area.</p>
      </div>

      <div className="section">
        <h3 className="section-title">
          <span className="section-num">01</span>
          Area de interes
        </h3>
        <div className="field">
          <label htmlFor="production-aoi">Sitio disponible</label>
          <select
            id="production-aoi"
            value={selectedAoiId}
            onChange={(e) => onSelectAoi(e.target.value)}
            disabled={busy['load-production-aoi']}
          >
            {localAois.length === 0 && <option value="">No hay sitios configurados</option>}
            {localAois.map((aoi) => (
              <option key={aoi.id} value={aoi.id}>{cleanAoiName(aoi.name)}</option>
            ))}
          </select>
        </div>
        <div className="row">
          <button className="btn" type="button" onClick={onRefreshAois}>Actualizar sitios</button>
          <button className="btn" type="button" onClick={onRefreshProducts}>Actualizar datos</button>
        </div>
        {busy['load-production-aoi'] && <div className="kml-status"><Spinner size={12} inline /> Cargando sitio...</div>}
        {isDisplacementLoaded && (
          <div className="kml-status kml-ready">
            Mapa de desplazamiento listo para visualizar y analizar.
          </div>
        )}
      </div>

      <div className="section">
        <h3 className="section-title">
          <span className="section-num">02</span>
          Mapa de desplazamiento
        </h3>
        <div className="map-product-control">
          <div className="field map-product-field">
            <label htmlFor="production-product">Producto publicado</label>
            <select
              id="production-product"
              value={productionProductId}
              disabled={productionProducts.length <= 1 || busy['load-production-product']}
              onChange={(e) => onProductChange(e.target.value)}
            >
              {productionProducts.length === 0 && <option value="">No hay producto procesado</option>}
              {productionProducts.map((product) => (
                <option key={product.local_id} value={product.local_id}>
                  {latestProductText(product)}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="map-product-action"
            disabled={!selectedProduct || busy['load-production-product']}
            onClick={() => onToggleDisplacement(true)}
          >
            {busy['load-production-product'] ? <Spinner size={12} inline /> : 'Ver'}
          </button>
        </div>
        <div className={`map-product-status ${isDisplacementLoaded ? 'ready' : ''}`}>
          {isDisplacementLoaded && productionProductId
            ? `Activo: ${productionProductId}`
            : selectedProduct
              ? 'Listo para cargar'
              : 'Sin producto para este sitio'}
        </div>        <details className="production-accordion">
          <summary>Configurar colores del mapa</summary>
          <div className="visual-tuning production-tuning">
            {staticBuild && (
              <p className="static-export-hint">
                Vista estatica: si el export incluye raster bruto o stack temporal, estos controles recalculan el mapa en el navegador.
              </p>
            )}
            <div className="visual-tuning-head muted-head">
              <span>Periodo y escala de datos</span>
            </div>
            <div className="date-grid">
              <div className="field compact">
                <label htmlFor="production-start-date">Inicio</label>
                <input
                  id="production-start-date"
                  type="date"
                  value={startDate}
                  min={selectedProduct?.start_date ?? undefined}
                  max={endDate}
                  disabled={scaleControlsDisabled}
                  onChange={(e) => onDateRangeChange(e.target.value, endDate)}
                />
              </div>
              <div className="field compact">
                <label htmlFor="production-end-date">Fin</label>
                <input
                  id="production-end-date"
                  type="date"
                  value={endDate}
                  min={startDate}
                  max={selectedProduct?.end_date ?? undefined}
                  disabled={scaleControlsDisabled}
                  onChange={(e) => onDateRangeChange(startDate, e.target.value)}
                />
              </div>
            </div>
            <small className="field-hint">
              Si el export incluye stack temporal, el mapa se recalcula para este periodo offline. Si no, se usa el raster publicado.
            </small>
            <VisualSlider
              label="Umbral critico"
              value={colorThresholdCmYr}
              min={0}
              max={12}
              step={0.05}
              unit=" cm/yr"
              onChange={setColorThresholdCmYr}
              disabled={scaleControlsDisabled}
            />
            <VisualSlider
              label="Maximo de color"
              value={colorMaxCmYr}
              min={0.5}
              max={20}
              step={0.1}
              unit=" cm/yr"
              onChange={setColorMaxCmYr}
              disabled={scaleControlsDisabled}
            />
            <label className={`checkbox-label${scaleControlsDisabled ? ' checkbox-label-disabled' : ''}`}>
              <input
                type="checkbox"
                checked={filterBelowThreshold}
                disabled={scaleControlsDisabled}
                onChange={(e) => setFilterBelowThreshold(e.target.checked)}
              />
              Ocultar valores bajo el umbral
            </label>
            <button
              type="button"
              className="btn btn-primary"
              disabled={scaleControlsDisabled || busy['apply-color-scale']}
              onClick={onApplyColorScale}
            >
              {busy['apply-color-scale'] ? <Spinner size={12} inline /> : null}
              Aplicar colores
            </button>
            <div className="visual-tuning-head muted-head">
              <span>Ajuste visual (capa en el mapa)</span>
              <button
                type="button"
                className="mini-btn"
                disabled={!isDisplacementLoaded}
                onClick={onResetVisualFilters}
              >
                Reiniciar
              </button>
            </div>
            <VisualSlider
              label="Contraste"
              value={layerContrast}
              min={60}
              max={220}
              unit="%"
              onChange={setLayerContrast}
              disabled={!isDisplacementLoaded}
            />
            <VisualSlider
              label="Saturacion"
              value={layerSaturation}
              min={40}
              max={240}
              unit="%"
              onChange={setLayerSaturation}
              disabled={!isDisplacementLoaded}
            />
          </div>
        </details>
      </div>

      <div className="section production-actions">
        <h3 className="section-title">
          <span className="section-num">03</span>
          Analisis
        </h3>
        <div className="analysis-step">
          <span className="step-index">1</span>
          <div>
            <strong>Dibujar poligono</strong>
            <small>Marca el area directamente sobre el mapa de desplazamiento.</small>
          </div>
        </div>
        <div className="row">
          <button
            type="button"
            className="btn"
            onClick={onStartPolygon}
            disabled={!isDisplacementLoaded}
          >
            Dibujar poligono
          </button>
          <button
            type="button"
            className="btn"
            onClick={onFinishPolygon}
            disabled={!isDisplacementLoaded}
          >
            Finalizar
          </button>
        </div>
        <div className="analysis-step">
          <span className="step-index">2</span>
          <div>
            <strong>Analizar puntos</strong>
            <small>La consulta usa solamente los puntos dentro del poligono cerrado.</small>
          </div>
        </div>
        <ActionButton
          label="Analizar poligono"
          busy={busy['analyze-polygon']}
          onClick={onAnalyzePolygon}
          disabled={!drawnPolygon}
          primary
          icon="*"
        />
        <div className="polygon-status">
          {!drawnPolygon
            ? <span className="poly-hint">Dibuja un poligono en el mapa para habilitar el analisis.</span>
            : <span className="poly-ready">Poligono listo ({drawnPolygon.length} vertices)</span>
          }
        </div>
        {polygonResult && (
          <div className="polygon-summary compact">
            <div className="poly-stat">
              <span>Puntos</span>
              <strong>{polygonResult.n_points}</strong>
            </div>
            <div className="poly-stat">
              <span>Area</span>
              <strong>{polygonResult.polygon_area_km2.toFixed(2)} km2</strong>
            </div>
            <div className="poly-stat">
              <span>Velocidad media</span>
              <strong className={polygonResult.mean_velocity_cm_yr < 0 ? 'v-neg' : 'v-pos'}>
                {polygonResult.mean_velocity_cm_yr > 0 ? '+' : ''}{polygonResult.mean_velocity_cm_yr.toFixed(2)} cm/yr
              </strong>
            </div>
          </div>
        )}
        <label className="switch-row interferogram-toggle">
          <span>
            <strong>Interferograma</strong>
            <small>Capa visual opcional. Puedes apagarla para volver al desplazamiento sin perder los puntos.</small>
          </span>
          <input
            type="checkbox"
            checked={interferogramEnabled}
            disabled={!hasLoadedAoi || busy['compute-interferogram'] || busy['load-production-displacement']}
            onChange={(e) => onToggleInterferogram(e.target.checked)}
          />
        </label>
        {busy['compute-interferogram'] && <div className="kml-status"><Spinner size={12} inline /> Cargando interferograma...</div>}
      </div>
    </div>
  );
}

function bboxIntersectionArea(a: [number, number, number, number], b: [number, number, number, number]): number {
  const minLon = Math.max(a[0], b[0]);
  const minLat = Math.max(a[1], b[1]);
  const maxLon = Math.min(a[2], b[2]);
  const maxLat = Math.min(a[3], b[3]);
  return Math.max(0, maxLon - minLon) * Math.max(0, maxLat - minLat);
}

function bboxArea(bbox: [number, number, number, number]): number {
  return Math.max(0, bbox[2] - bbox[0]) * Math.max(0, bbox[3] - bbox[1]);
}

function productOverlapScore(p: StoredProduct, aoiBbox: [number, number, number, number]): number {
  if (!p.bbox) return -1;
  return bboxIntersectionArea(p.bbox, aoiBbox) / Math.max(bboxArea(aoiBbox), 1e-12);
}

function latestProductForBbox(products: StoredProduct[], aoiBbox: [number, number, number, number]): StoredProduct | null {
  return products
    .filter((p) => p.bbox && productOverlapScore(p, aoiBbox) > 0)
    .sort((a, b) => {
      const overlapDiff = productOverlapScore(b, aoiBbox) - productOverlapScore(a, aoiBbox);
      if (Math.abs(overlapDiff) > 0.05) return overlapDiff;
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    })[0] ?? null;
}

function cleanAoiName(name: string): string {
  return name.replace(/\.(kml|kmz)$/i, '').trim();
}

function latestProductText(product: StoredProduct): string {
  const component = product.has_vertical ? 'Vertical' : product.has_los ? 'LOS' : 'Raster';
  const dates = product.start_date && product.end_date ? `${product.start_date} -> ${product.end_date}` : 'sin rango de fechas';
  const res = product.native_resolution_m ? `${product.native_resolution_m.toFixed(0)} m` : 'resolucion no detectada';
  return `${component} - ${dates} - ${res}`;
}

function ringIntersectsBBox(ring: [number, number][], bbox: [number, number, number, number]): boolean {
  const ringBounds = ringBBox(ring);
  return !(
    ringBounds[2] < bbox[0] ||
    ringBounds[0] > bbox[2] ||
    ringBounds[3] < bbox[1] ||
    ringBounds[1] > bbox[3]
  );
}

function ringBBox(ring: [number, number][]): [number, number, number, number] {
  const lons = ring.map((p) => p[0]);
  const lats = ring.map((p) => p[1]);
  return [
    Math.min(...lons),
    Math.min(...lats),
    Math.max(...lons),
    Math.max(...lats),
  ];
}

function pointInRing(point: [number, number], ring: [number, number][]): boolean {
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersects = ((yi > y) !== (yj > y))
      && (x < ((xj - xi) * (y - yi)) / ((yj - yi) || Number.EPSILON) + xi);
    if (intersects) inside = !inside;
  }
  return inside;
}

function formatProductLabel(p: StoredProduct, aoiBbox: [number, number, number, number]): string {
  const modes = [
    p.has_vertical ? 'VERT' : '',
    p.has_los ? 'LOS' : '',
    p.has_coherence ? 'COH' : '',
  ].filter(Boolean).join('+');
  const dates = p.start_date && p.end_date ? ` | ${p.start_date} -> ${p.end_date}` : '';
  const resolution = p.native_resolution_m ? ` | ${p.native_resolution_m.toFixed(0)} m` : '';
  const overlap = p.bbox ? ` | coincidencia AOI ${Math.round(productOverlapScore(p, aoiBbox) * 100)}%` : ' | sin ubicacion';
  return `${p.name}${dates}${resolution}${overlap} (${modes || 'rasteres'})`;
}

interface ActionButtonProps {
  id?: string;
  label: string;
  busy?: boolean;
  primary?: boolean;
  disabled?: boolean;
  icon?: string;
  onClick: () => void;
}

function ActionButton({ id, label, busy, primary, disabled, icon, onClick }: ActionButtonProps) {
  return (
    <button
      id={id}
      className={`btn ${primary ? 'btn-primary' : ''}`}
      onClick={onClick}
      disabled={busy || disabled}
    >
      {busy ? <Spinner size={12} inline /> : icon ? <span className="btn-icon">{icon}</span> : null}
      <span>{label}</span>
    </button>
  );
}

function TrackBadge({ direction, count }: { direction: 'ASC' | 'DESC'; count: number }) {
  return (
    <span className={`track-badge ${direction === 'ASC' ? 'asc' : 'desc'}`}>
      {direction === 'ASC' ? 'ASC' : 'DESC'} {count}
    </span>
  );
}

function VisualSlider({
  label,
  value,
  min,
  max,
  step = 1,
  unit,
  onChange,
  disabled = false,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit: string;
  onChange: (value: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className={`visual-slider${disabled ? ' visual-slider-disabled' : ''}`}>
      <label>
        <span>{label}</span>
        <strong>{value}{unit}</strong>
      </label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

function LayerMeta({ state }: { state: AppStateShape }) {
  const { layer } = state;
  if (layer.kind === 'NONE') return null;
  const overlay =
    layer.kind === 'RGB' ? layer.data.overlay :
    layer.kind === 'INDEX' ? layer.data.overlay :
    layer.data.overlay;

  return (
    <div className="layer-meta">
      <div className="kv-grid">
        <div className="k">Modo</div>
        <div className="v">
          {layer.kind === 'RGB' ? layer.data.mode :
            layer.kind === 'INDEX' ? layer.data.mode :
            layer.data.mode}
        </div>
        <div className="k">Px nativo</div>
        <div className="v">{overlay.native_resolution_m.toFixed(1)} m</div>
        <div className="k">Px renderizado</div>
        <div className="v">{overlay.rendered_resolution_m.toFixed(1)} m</div>
        {layer.kind === 'DISPLACEMENT' && (
          <>
            <div className="k">Velocidad media</div>
            <div className="v">{layer.data.mean_velocity.toFixed(2)} cm/yr</div>
            <div className="k">Sigma velocidad</div>
            <div className="v">{layer.data.stdev_velocity.toFixed(2)} cm/yr</div>
            <div className="k">Orbitas</div>
            <div className="v">{layer.data.tracks_used.join(' + ')}</div>
          </>
        )}
        {layer.kind === 'INTERFEROGRAM' && (
          <>
            <div className="k">Par</div>
            <div className="v">{layer.data.pair_label}</div>
            <div className="k">Linea base</div>
            <div className="v">{layer.data.temporal_baseline_days} dias</div>
            <div className="k">Coh. media</div>
            <div className="v">{layer.data.coherence_mean.toFixed(3)}</div>
          </>
        )}
      </div>
      <div className="resampling-note">
        <strong>Nota de resolucion.</strong> {overlay.resampling_note}
      </div>
      {layer.kind === 'DISPLACEMENT' && layer.data.caveats.length > 0 && (
        <details className="caveats">
          <summary>Metodo + advertencias</summary>
          <div className="method">{layer.data.method}</div>
          <ul>
            {layer.data.caveats.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </details>
      )}
      {layer.kind === 'INTERFEROGRAM' && layer.data.caveats.length > 0 && (
        <details className="caveats">
          <summary>Notas del interferograma</summary>
          <ul>
            {layer.data.caveats.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}
